import { useUser } from "../hooks/useUser"
import { useAppSelector, useAppDispatch } from '../hooks/reduxToolkit'
import { Unauthorized } from '../screens/unauthorized'
import { BlobRow } from "../../types/supabase"
import { useClient } from "../providers/supabase"
import { useEffect, useState, useCallback, useRef, useMemo } from "react"
import { useIntersectionObserver } from "../hooks/useIntersectionObserver"
import { BlobPdfPreview } from "../components/preview/blobPdfPreview"
import { ContextMenu, ContextMenuItem, ContextMenuDivider } from "../components/ui/contextMenu"
import { useConfirm } from "../providers/confirmDialog"
import { SnapAPhoto } from "../components/snapAPhoto"
import { Tooltip } from "../components/tooltip"
import { present } from "../../lib/util/present"
import * as Sentry from "@sentry/react"
import { useNavigate } from "react-router"
import { extractExpenseFromTextract } from "../../lib/util/extractExpenseFromTextract"
import { formatDateInTimeZone } from "../../lib/formatDateInTimeZone"
import { useReceiptSearch } from "../hooks/useReceiptSearch"
import { ReceiptSearchNavigation } from "../components/receiptSearchNavigation"
import { billsForwardingAddress } from "@healthshare/shared"
import { FilePreviewModal } from "../components/filePreview"
import { CopyableSpan } from "../components/copyableSpan"

import './receiptLibrary.scss'
import { BlobModel, deleteBlob } from "../reduxToolkit/blobsSlice"

// Helper functions for Textract job status (mirrored from expensesSlice)
function isBlobTextractPending(blob: BlobRow | BlobModel): boolean {
  return present(blob.textract_job_started_at) &&
    !present(blob.textract_job_completed_at)
}

// 10 minutes timeout (same as expense logic)
const TEXTRACT_TIMEOUT_MILLISECONDS = 10 * 60 * 1000
function isBlobTextractJobFailed(blob: BlobRow | BlobModel): boolean {
  if (!present(blob.textract_job_status)) { return false }
  if (blob.textract_job_status === 'FAILED') { return true }
  
  if (!present(blob.textract_job_completed_at) && present(blob.textract_job_started_at)) {
    return Date.parse(blob.textract_job_started_at) < (Date.now() - TEXTRACT_TIMEOUT_MILLISECONDS)
  }
  return false
}

export function ReceiptLibrary() {
  const user = useUser()
  const client = useClient()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const attachments = useAppSelector((s) => s.attachments.attachments)
  const membershipId = useAppSelector((s) => s.membership.membershipId)
  const [filterType, setFilterType] = useState<'all' | 'receipt' | 'itemized-bill'>('all')
  const [previewBlob, setPreviewBlob] = useState<BlobRow | null>(null)

  // Get all blobs from Redux that are receipt library eligible and have no non-message attachments
  const allBlobs = useAppSelector((s) => s.blobs.blobs)
  const blobs = useMemo(() => {
    // Filter out blobs that have non-message attachments (message attachments from email import don't count)
    const attachedBlobKeys = new Set(
      attachments
        .filter((a: any) => a.table_name !== 'messages')
        .map((a: any) => a.blob_key)
    )

    return allBlobs
      .filter((blob: any) =>
        blob.is_receipt_library_eligible &&
        !blob.deleted_at &&
        !attachedBlobKeys.has(blob.key)
      )
      .sort((a: any, b: any) => b.created_at.localeCompare(a.created_at))
  }, [allBlobs, attachments])

  // Search functionality
  const [
    searchResults,
    search,
    { query }
  ] = useReceiptSearch(blobs || [])

  // Filter receipts by type and combine with search results
  const getFilteredResults = () => {
    const baseResults = searchResults || blobs || []

    if (filterType === 'all') {
      return baseResults
    }

    return baseResults.filter((blob: any) =>
      blob.blob_original_purpose === filterType
    )
  }

  const deleteReceipt = useCallback((blob: BlobRow | BlobModel) => {
    const now = new Date().toISOString()

    // Dispatch deleteBlob action - sync engine will handle database update
    dispatch(deleteBlob({
      id: blob.id,
      updated_at: now,
      deleted_at: now
    }))
  }, [dispatch])

  if (!user) {
    return <Unauthorized />
  }

  return (
    <div className="row receipt-library">
      <div className="col-12">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex flex-column gap-3">
            <h1>Attachments</h1>
            <p className="mb-2 text-muted">
              Email bills directly to<br/>
              <CopyableSpan className="fw-bold text-primary">{billsForwardingAddress(import.meta.env.VITE_BILLS_FORWARDING_ADDRESS)}</CopyableSpan><br/>
              to automatically import them to your attachments.
            </p>
          </div>
        </div>
          
        <div className="d-flex justify-content-between align-items-center mb-4">
            {/* Capture is local-first: bytes upload asynchronously, and the content
                endpoint triggers Textract processing itself the moment they land -- no
                client-side "start processing now" ping needed anymore. */}
            <SnapAPhoto
              saveToReceiptLibrary={true}
              showPurposeSelection={false}
            >
              <button className="btn btn-primary d-flex align-items-center gap-2">
                Add Attachment
              </button>
            </SnapAPhoto>

            <div className="text-muted">
              {blobs?.length || 0} attachments
            </div>
        </div>

        {blobs && (
          <>
            <ReceiptSearchNavigation 
              fuseSearch={{
                query,
                search: (newQuery) => {
                  search(newQuery)
                }
              }}
              filter={{
                value: filterType,
                onChange: (filter) => setFilterType(filter)
              }}
            />


            {blobs.length === 0 ? (
              <div className="empty-state text-center py-5">
                <i className="material-icons mb-3" style={{ fontSize: '48px' }}>receipt</i>
                <h3>No receipts in your library</h3>
                <p className="text-muted">
                  Take photos of receipts using the camera, and they'll appear here for you to attach to expenses later.
                </p>
              </div>
            ) : (
              <>
                {(query || filterType !== 'all') && (
                  <div className="mb-3">
                    <h6>
                      {getFilteredResults().length} 
                      {query && filterType !== 'all' 
                        ? `search result${getFilteredResults().length !== 1 ? 's' : ''} for "${query}" in ${filterType === 'receipt' ? 'receipts' : 'itemized bills'}` 
                        : query 
                          ? `search result${getFilteredResults().length !== 1 ? 's' : ''} for "${query}"` 
                          : `${filterType === 'receipt' ? 'receipt' : 'itemized bill'}${getFilteredResults().length !== 1 ? 's' : ''}`
                      }
                    </h6>
                  </div>
                )}
                <div className="receipt-grid">
                  {getFilteredResults().map((blob: any) => (
                    <ReceiptCard
                      key={blob.key}
                      blob={blob}
                      onDelete={() => deleteReceipt(blob)}
                      navigate={navigate}
                      onDoubleClick={() => setPreviewBlob(blob)}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
      
      <FilePreviewModal
        show={!!previewBlob}
        fileOrBlob={previewBlob}
        onClose={() => setPreviewBlob(null)}
      />
    </div>
  )
}

interface ReceiptCardProps {
  blob: BlobRow
  onDelete: () => void
  navigate: (path: string, options?: any) => void
  onDoubleClick: () => void
}

// Exported for receiptLibrary.test.tsx, which drives the preview-loading states
// (uploaded / still-uploading / genuinely broken) directly rather than through the
// whole library screen.
export function ReceiptCard({ blob, onDelete, navigate, onDoubleClick }: ReceiptCardProps) {
  const client = useClient()
  const [confirm] = useConfirm()
  const [imageUrl, setImageUrl] = useState<string>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const [awaitingUpload, setAwaitingUpload] = useState(false)
  const [contextMenu, setContextMenu] = useState<{ isOpen: boolean; x: number; y: number }>({
    isOpen: false,
    x: 0,
    y: 0
  })
  const { elementRef, hasIntersected } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '100px'
  })
  const imageUrlRef = useRef<string>()

  useEffect(() => {
    if (!hasIntersected) return

    const loadPreview = async () => {
      console.log('loadPreview', blob.file_full_path)
      setLoading(true)
      setError(undefined) // Clear any previous errors
      setAwaitingUpload(false)

      // Capture is local-first: a blobs row exists as soon as the member takes the
      // photo, but the bytes only reach storage on the device's next connection
      // (BlobModel.bytes_uploaded_at). Downloading before then 404s, which is
      // expected rather than broken -- so the object being missing means "still
      // uploading" for these rows, not "failed", and there is nothing to retry.
      //
      // Deliberately not `blob.bytes_uploaded_at === null`: for a row rehydrated
      // from redux-persist that predates the column, the field is `undefined` even
      // though the bytes are long since uploaded (see CLAUDE.md, "Redux Persist and
      // Stale Schema"). We therefore still attempt the download for those, and only
      // treat a 404 as pending -- never as a reason to skip the request.
      const bytesKnownUploaded = present(blob.bytes_uploaded_at)
      const maxRetries = bytesKnownUploaded ? 5 : 1
      let attempt = 0

      while (attempt < maxRetries) {
        try {
          const download = await client.storage
            .from('wcc-hra-calculator-private')
            .download(blob.file_full_path)

          if (download.error) {
            // Check if it's a 404 "Object not found" error - this can happen if blob upload is still in progress
            if (download.error.message.includes('not found') && attempt < maxRetries - 1) {
              const backoffDelay = Math.pow(2, attempt) * 500 // 500ms, 1s, 2s, 4s, 8s
              console.log(`Blob not found (attempt ${attempt + 1}/${maxRetries}), retrying in ${backoffDelay}ms...`)
              await new Promise(resolve => setTimeout(resolve, backoffDelay))
              attempt++
              continue
            }
            if (download.error.message.includes('not found') && !bytesKnownUploaded) {
              // The bytes have not landed yet. Show a pending state and stay quiet --
              // reporting this would fill Sentry with normal upload latency.
              setAwaitingUpload(true)
              setLoading(false)
              return
            }
            throw new Error(download.error.message)
          }

          if (!download.data) {
            throw new Error('No data received')
          }

          const url = URL.createObjectURL(download.data)
          imageUrlRef.current = url
          setImageUrl(url)
          setLoading(false)
          return // Success! Exit the retry loop
        } catch (err) {
          // If this was the last attempt, set the error
          if (attempt >= maxRetries - 1) {
            // A receipt whose bytes we believe are in storage but that we cannot
            // download is a real failure, and the member only ever saw a red icon
            // for it -- report it rather than letting it stay invisible.
            Sentry.captureException(err, {
              tags: { feature: 'receipt-library', receipt_op: 'load-preview' },
              extra: {
                blob_key: blob.key,
                file_full_path: blob.file_full_path,
                bytes_uploaded_at: blob.bytes_uploaded_at ?? null,
              },
            })
            setError(err instanceof Error ? err.message : 'Failed to load preview')
            setLoading(false)
            return
          }

          // Otherwise, retry with exponential backoff
          const backoffDelay = Math.pow(2, attempt) * 500
          console.log(`Error loading preview (attempt ${attempt + 1}/${maxRetries}):`, err, `retrying in ${backoffDelay}ms...`)
          await new Promise(resolve => setTimeout(resolve, backoffDelay))
          attempt++
        }
      }
    }

    loadPreview()

    // Cleanup URL when effect runs again or component unmounts
    return () => {
      if (imageUrlRef.current) {
        URL.revokeObjectURL(imageUrlRef.current)
        imageUrlRef.current = undefined
      }
    }
    // bytes_uploaded_at is a dependency so that a receipt parked in the pending
    // state re-attempts its preview as soon as the upload lands and syncs back down.
  }, [hasIntersected, blob.file_full_path, blob.bytes_uploaded_at])

  const handleMenuClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const buttonRect = event.currentTarget.getBoundingClientRect()
    setContextMenu({
      isOpen: true,
      x: buttonRect.left - 200 + buttonRect.width, // Position menu to the left of button
      y: buttonRect.top - 10 // Position menu above button
    })
  }, [])

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, isOpen: false }))
  }, [])

  const handleCreateExpense = useCallback(() => {
    handleCloseContextMenu()
    
    // Extract expense data from Textract results if available
    const extractedData = extractExpenseFromTextract(blob)
    
    // Navigate to new expense form with pre-populated data and blob key for attachment
    navigate('/expenses/new', { 
      state: { 
        prefilledData: extractedData || {},
        blobKey: blob.key
      } 
    })
  }, [blob, navigate, handleCloseContextMenu])

  const handleAttachToExpense = useCallback(() => {
    handleCloseContextMenu()
    
    // Navigate to expense selection page with blob key
    navigate(`/receipts/${blob.key}/attach-to-expense`)
  }, [blob.key, navigate, handleCloseContextMenu])

  const handleDelete = useCallback(async () => {
    handleCloseContextMenu()
    
    const confirmed = await confirm({
      title: 'Delete Receipt',
      message: (
        <>
          <p>Are you sure you want to delete <strong>{blob.file_name}</strong>?</p>
          <p className="text-muted mb-0">This action cannot be undone.</p>
        </>
      ),
      actions: [
        {
          title: 'Cancel',
          style: 'secondary',
          isCancel: true,
        },
        {
          title: 'Delete',
          style: 'danger',
          isConfirm: true,
        }
      ]
    })
    
    if (confirmed) {
      try {
        onDelete()
      } catch (err) {
        console.error('Failed to delete receipt:', err)
        Sentry.captureException(err)
      }
    }
  }, [confirm, blob.file_name, onDelete, handleCloseContextMenu])

  const getDisplayDate = (blob: BlobRow) => {
    const textractData = extractExpenseFromTextract(blob)
    if (textractData?.date) {
      return formatDateInTimeZone(textractData.date, { format: 'MMM dd, yyyy' })
    }
    return new Date(blob.created_at).toLocaleDateString()
  }

  const renderTextractDetails = (blob: BlobRow) => {
    const textractData = extractExpenseFromTextract(blob)
    if (!textractData) return null

    // Show summary first if available, then other fields
    const hasSummary = textractData.summary
    const hasProvider = textractData.provider
    const hasAmount = textractData.listedAmount || textractData.paidAmount

    if (!hasSummary && !hasProvider && !hasAmount) return null

    return (
      <div className="receipt-textract-details">
        {hasSummary && (
          <div className="textract-field" title={`Summary: ${textractData.summary}`}>
            <i className="material-icons">description</i>
            <span>{textractData.summary}</span>
          </div>
        )}
        {hasProvider && (
          <div className="textract-field" title={`Provider: ${textractData.provider}`}>
            <i className="material-icons">business</i>
            <span>{textractData.provider}</span>
          </div>
        )}
        {hasAmount && (
          <div className="textract-field" title={`Amount: $${textractData.listedAmount || textractData.paidAmount}`}>
            <i className="material-icons">attach_money</i>
            <span>{textractData.listedAmount || textractData.paidAmount}</span>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <div ref={elementRef} className="receipt-card">
        <div className="receipt-card-preview" onDoubleClick={onDoubleClick}>
        {!hasIntersected && (
          <div className="d-flex justify-content-center align-items-center h-100 text-muted">
            <i className="material-icons">visibility</i>
          </div>
        )}
        
        {hasIntersected && loading && (
          <div className="d-flex justify-content-center align-items-center h-100">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
        
        {hasIntersected && awaitingUpload && (
          <Tooltip tooltip="This receipt hasn't finished uploading yet. It will appear once the device it was captured on is back online.">
            <div className="d-flex justify-content-center align-items-center h-100 text-muted">
              <i className="material-icons">cloud_upload</i>
            </div>
          </Tooltip>
        )}

        {hasIntersected && error && !awaitingUpload && (
          <Tooltip tooltip="We couldn't load this receipt. We've been notified and are looking into it.">
            <div className="d-flex justify-content-center align-items-center h-100 text-danger">
              <i className="material-icons">error</i>
            </div>
          </Tooltip>
        )}

        {hasIntersected && imageUrl && !loading && (
          <>
            {blob.content_type === 'application/pdf' ? (
              <BlobPdfPreview blobUrl={imageUrl} fileName={blob.file_name} />
            ) : (
              <img
                src={imageUrl}
                alt={blob.file_name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'top'
                }}
              />
            )}
          </>
        )}
        
        {/* Status icons in top right corner */}
        <div className="receipt-status-icons">
          {isBlobTextractPending(blob) && !isBlobTextractJobFailed(blob) && (
            <Tooltip tooltip="Scanning receipt to extract information. This can sometimes take a few minutes.">
              <i className="material-icons flicker text-primary">sensors</i>
            </Tooltip>
          )}
          {isBlobTextractJobFailed(blob) && (
            <Tooltip tooltip="Unable to automatically extract receipt information">
              <i className="material-icons text-danger">error</i>
            </Tooltip>
          )}
        </div>
      </div>
      
        <div className="receipt-card-info">
          <div className="receipt-card-filename" title={blob.file_name}>
            {blob.file_name}
          </div>
          <div className="receipt-card-date text-muted">
            {getDisplayDate(blob)}
          </div>
          {renderTextractDetails(blob)}
        </div>
        
        <button 
          className="receipt-card-menu-button"
          onClick={handleMenuClick}
          aria-label="Receipt actions"
        >
          <i className="material-icons">more_vert</i>
        </button>
      </div>

    <ContextMenu
      isOpen={contextMenu.isOpen}
      onClose={handleCloseContextMenu}
      position={{ x: contextMenu.x, y: contextMenu.y }}
    >
      <ContextMenuItem onClick={handleCreateExpense} icon="add_shopping_cart">
        Create Expense from Receipt
      </ContextMenuItem>
      <ContextMenuItem onClick={handleAttachToExpense} icon="attach_file">
        Attach to Existing Expense
      </ContextMenuItem>
      <ContextMenuDivider />
      <ContextMenuItem onClick={handleDelete} icon="delete" variant="danger">
        Delete
      </ContextMenuItem>
    </ContextMenu>
  </>
  )
}